import React, { useEffect, useMemo } from "react";
import { View, StyleSheet } from "react-native";
import { useRoute, useNavigation } from "@react-navigation/native";
import {
  QuestionLayout,
  IconInput,
} from "@src/packages/medical-history/components";
import { MoonSelectField } from "@louise/moon-ui";
import { MapPin, FileText } from "lucide-react-native";
import { useTranslations } from "@src/packages/i18n";
import {
  useExposure,
  ExposureFrequency,
  ExposureType,
} from "../ExposureContext";
import type { ExposureStackScreenProps } from "../ExposureNavigator";
import { EXPOSURE_FREQUENCY_OPTIONS, EXPOSURE_OPTIONS } from "../options";

export function ExposureDetailsScreen() {
  const t = useTranslations();
  const route =
    useRoute<ExposureStackScreenProps<"ExposureDetails">["route"]>();
  const navigation =
    useNavigation<ExposureStackScreenProps<"ExposureDetails">["navigation"]>();
  const routeExposure = (
    route.params as { exposure?: ExposureType } | undefined
  )?.exposure;

  const { state, updateState, next, isNextDisabled } = useExposure({
    name: "ExposureDetails",
    params: routeExposure ? { exposure: routeExposure } : undefined,
  });

  // The exposure is carried in navigation state, which a reload of this step
  // does not restore. Falling back to the first exposure this flow's own stack
  // details here keeps the step on the record instead of on nothing.
  const exposure =
    routeExposure ??
    state.navigationStack.find((entry) => entry.name === "ExposureDetails")
      ?.params?.exposure;

  useEffect(() => {
    if (routeExposure || !exposure) return;
    navigation.setParams({ exposure });
  }, [routeExposure, exposure, navigation]);

  const detail = useMemo(
    () => state.exposureDetails.find((item) => item.exposure === exposure),
    [state.exposureDetails, exposure],
  );

  useEffect(() => {
    if (exposure && !detail) {
      updateState({
        exposureDetails: state.exposureDetails.concat({
          id: undefined,
          exposure,
          frequency: null,
          context: null,
          other: null,
        }),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail, exposure]);

  const optionLabel = useMemo(() => {
    const option = EXPOSURE_OPTIONS.find((opt) => opt.id === exposure);
    return option
      ? t(option.labelKey)
      : t("med_history.lifestyle.exposure.details_default_title");
  }, [exposure, t]);

  const updateDetail = (partial: {
    frequency?: ExposureFrequency | null;
    context?: string | null;
    other?: string | null;
  }) => {
    updateState({
      exposureDetails: state.exposureDetails.map((item) => {
        if (item.exposure === exposure) {
          return { ...item, ...partial };
        }
        return item;
      }),
    });
  };

  const handleSave = () => {
    next();
  };

  const frequencyValue = detail?.frequency ?? null;
  const contextValue = detail?.context ?? "";
  const otherValue = detail?.other ?? "";

  const isOtherExposure = exposure === "other_exposure";

  return (
    <QuestionLayout
      title={optionLabel}
      subtitle={t("med_history.lifestyle.exposure.details_subtitle")}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={!frequencyValue || isNextDisabled}
      saveLabel={t("med_history.lifestyle.continue")}
    >
      <View style={styles.formContainer}>
        <View style={styles.inputGroup}>
          <MoonSelectField
            label={t("med_history.lifestyle.exposure.frequency_label")}
            placeholder={t(
              "med_history.lifestyle.exposure.frequency_placeholder",
            )}
            value={frequencyValue}
            onChange={(value) =>
              updateDetail({
                frequency: (value as ExposureFrequency | null) ?? null,
              })
            }
            options={EXPOSURE_FREQUENCY_OPTIONS.map((option) => ({
              label: t(option.labelKey),
              value: option.id,
            }))}
          />
        </View>

        <View style={styles.inputGroup}>
          <IconInput
            label={t("med_history.lifestyle.exposure.context_label")}
            icon={MapPin}
            placeholder={t(
              "med_history.lifestyle.exposure.context_placeholder",
            )}
            value={contextValue}
            onChangeText={(text) =>
              updateDetail({ context: text.length > 0 ? text : null })
            }
          />
        </View>

        {isOtherExposure ? (
          <View style={styles.inputGroup}>
            <IconInput
              label={t("med_history.lifestyle.exposure.other_label")}
              icon={FileText}
              placeholder={t(
                "med_history.lifestyle.exposure.other_placeholder",
              )}
              value={otherValue}
              onChangeText={(text) =>
                updateDetail({ other: text.length > 0 ? text : null })
              }
            />
          </View>
        ) : null}
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  formContainer: {
    width: "100%",
    marginBottom: 40,
  },
  inputGroup: {
    marginBottom: 16,
  },
});
